import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { RotateCcw, X } from "lucide-react";
import "./RefundModal.scss";

/**
 * RefundModal — Refund confirmation dialog with reason and optional amount input.
 * Reuses the same visual pattern as ConfirmModal. Theme-aware via CSS variables.
 *
 * Props:
 *   show            — visibility flag
 *   onHide          — close handler
 *   onConfirm       — (reason, amount, revokeAccess) => void
 *   title           — modal title
 *   studentName     — name of the student
 *   originalAmount  — original payment amount
 *   maxRefundable   — max amount that can be refunded (originalAmount - alreadyRefunded)
 *   isOnline        — whether this is a Razorpay (online) payment
 *   loading         — disable buttons while processing
 *   courseName      — name of the course being refunded
 *   courseType      — 'self-paced' | 'batch' | null
 *   isFullRefundDefault — whether full refund is the default (used for revokeAccess default)
 */
const RefundModal = ({
  show,
  onHide,
  onConfirm,
  title = "Refund Payment",
  studentName = "",
  originalAmount = 0,
  maxRefundable = 0,
  isOnline = false,
  loading = false,
  error = "",
  courseName = "",
  courseType = null,
}) => {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [isPartial, setIsPartial] = useState(false);
  const [revokeAccess, setRevokeAccess] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setReason("");
      setAmount("");
      setIsPartial(false);
      // Default: revoke access ON for self-paced full refund, OFF for live courses
      setRevokeAccess(courseType === 'self-paced');
    }
  }, [show, courseType]);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && !loading) onHide();
    },
    [onHide, loading]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const refundAmount = isPartial ? parseFloat(amount) || 0 : maxRefundable;
  const isValid =
    reason.trim().length > 0 &&
    refundAmount > 0 &&
    refundAmount <= maxRefundable;

  const handleConfirm = () => {
    if (!isValid || loading) return;
    onConfirm(reason.trim(), refundAmount, revokeAccess);
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val || 0);

  return createPortal(
    <div className="confirm-overlay" onClick={!loading ? onHide : undefined}>
      <div
        className="confirm-dialog refund-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="refund-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="confirm-dialog__close"
          onClick={onHide}
          disabled={loading}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Header — icon + title inline */}
        <div className="refund-dialog__header">
          <div className="refund-dialog__icon">
            <RotateCcw size={20} />
          </div>
          <div className="refund-dialog__header-text">
            <h3 className="refund-dialog__title" id="refund-dialog-title">
              {title}
            </h3>
            <p className="refund-dialog__subtitle">
              {formatCurrency(originalAmount)} · {studentName || "Student"} · {isOnline ? "Razorpay" : "Manual"}{courseName ? ` · ${courseName}` : ""}
            </p>
          </div>
        </div>

        <div className="refund-dialog__body">

          {/* Refund amount summary */}
          <div className="refund-dialog__summary">
            <div className="refund-dialog__summary-row">
              <span>Original Amount</span>
              <span>{formatCurrency(originalAmount)}</span>
            </div>
            <div className="refund-dialog__summary-row">
              <span>Max Refundable</span>
              <span>{formatCurrency(maxRefundable)}</span>
            </div>
            {isPartial && refundAmount > 0 && (
              <div className="refund-dialog__summary-row refund-dialog__summary-row--highlight">
                <span>Refund Amount</span>
                <span>{formatCurrency(refundAmount)}</span>
              </div>
            )}
          </div>

          {/* Partial refund toggle */}
          <label className="refund-dialog__toggle">
            <input
              type="checkbox"
              checked={isPartial}
              onChange={(e) => {
                setIsPartial(e.target.checked);
                if (!e.target.checked) setAmount("");
              }}
            />
            <span>Partial refund</span>
          </label>

          {/* Partial amount input */}
          {isPartial && (
            <div className="refund-dialog__field">
              <label className="refund-dialog__label">Refund Amount</label>
              <input
                type="number"
                className="refund-dialog__input"
                placeholder={`Max ₹${maxRefundable}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={1}
                max={maxRefundable}
                step="1"
              />
              {parseFloat(amount) > maxRefundable && (
                <span className="refund-dialog__error">
                  Cannot exceed ₹{maxRefundable}
                </span>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="refund-dialog__field">
            <label className="refund-dialog__label">
              Reason for refund <span>*</span>
            </label>
            <textarea
              className="refund-dialog__input"
              rows={3}
              placeholder="Enter reason for refunding this payment..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          </div>

          {isOnline && !error && (
            <p className="refund-dialog__note">
              The refund will be initiated via Razorpay and may take 5–7 business days to reflect in the student's account.
            </p>
          )}

          {/* Revoke access toggle */}
          {courseName && (
            <div className="refund-dialog__revoke">
              <label className="refund-dialog__toggle">
                <input
                  type="checkbox"
                  checked={revokeAccess}
                  onChange={(e) => setRevokeAccess(e.target.checked)}
                />
                <span>Remove student's access to <strong>{courseName}</strong></span>
              </label>
              {revokeAccess && (
                <p className="refund-dialog__revoke-warn">
                  The student will lose access to {courseType === 'self-paced' ? 'course content and progress' : 'this batch/course'}. This cannot be undone automatically.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="refund-dialog__error-box">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="confirm-dialog__actions">
          <button
            className="confirm-dialog__btn confirm-dialog__btn--cancel"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="confirm-dialog__btn confirm-dialog__btn--warning"
            onClick={handleConfirm}
            disabled={loading || !isValid}
          >
            {loading ? (
              <span className="confirm-dialog__spinner" />
            ) : null}
            {loading
              ? "Processing…"
              : `Refund ${formatCurrency(isPartial ? refundAmount : maxRefundable)}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RefundModal;
